import { t as localeText } from "@/i18n/translate";
import type { MediaToolOperation, MediaToolReference, MediaToolVariant } from "@/features/media-tools";
import { createThreeDDraft, preferredThreeDVariant, type SettingValue, type ThreeDDraft } from "./threeDState";

export type GeneratorMode = "image" | "text" | "none";
const GENERATOR_MODELS = ["3d.tripo-h3.1", "3d.hunyuan3d-rapid", "3d.seed3d-2", "3d.hunyuan3d-pro", "3d.meshy-7", "3d.hi3d-3-quality", "3d.hi3d-3-master"];
const VIEW_ORDER = ["front", "left", "right", "back"];

/** Launch scope: object generation only, backed by existing public contracts. */
export function generatorModels(items: MediaToolOperation[], mode: GeneratorMode) {
  return items.filter(item => item.category === "3d" && item.operation === (mode === "text" ? "text_to_mesh" : "image_to_mesh")
    && item.product_variants.length && GENERATOR_MODELS.includes(item.product_id))
    .sort((a, b) => GENERATOR_MODELS.indexOf(a.product_id) - GENERATOR_MODELS.indexOf(b.product_id));
}

export function generatorProfile(model?: MediaToolOperation, variantID?: string) {
  return variantID ? model?.product_variants.find(item => item.id === variantID) : model?.product_variants.find(item => item.label === "3D Generator") ?? preferredThreeDVariant(model);
}

export function createGeneratorDraft(variant?: MediaToolVariant): ThreeDDraft {
  const draft = createThreeDDraft(variant);
  const texture = variant?.settings.find(field => field.key === "texture_quality");
  if (texture?.enum?.includes("detailed")) draft.settings.texture_quality = "detailed";
  const face = variant?.settings.find(field => field.key === "face_limit");
  if (face) draft.settings.face_limit = Math.min(face.maximum ?? 1_000_000, Math.max(face.minimum ?? 1, 1_000_000));
  return draft;
}

export function generatorViews(single?: MediaToolVariant, multi?: MediaToolVariant): MediaToolReference[] {
  const front = single?.reference_inputs.find(input => input.media_kind === "image");
  if (!front) return [];
  const sides = (multi?.reference_inputs ?? []).filter(input => input.media_kind === "image" && (input.role ?? input.key) !== "front");
  return [{ ...front, key: "front", role: "front", get label() { return localeText("interface.front_dd19f988", { defaultValue: "Front" }); } }, ...sides]
    .sort((a, b) => VIEW_ORDER.indexOf(a.role ?? a.key) - VIEW_ORDER.indexOf(b.role ?? b.key));
}

/** Map the unified image form to one exact single-image or named-view contract. */
export function generatorRequestDraft(draft: ThreeDDraft, variant: MediaToolVariant): ThreeDDraft {
  const settings = { ...createThreeDDraft(variant).settings };
  const explicit: string[] = [];
  for (const field of variant.settings) {
    const value = draft.settings[field.key];
    if (value === undefined || field.enum && !field.enum.includes(String(value))) continue;
    settings[field.key] = value;
    if (draft.explicit.includes(field.key)) explicit.push(field.key);
  }
  const sources: ThreeDDraft["sources"] = {};
  for (const input of variant.reference_inputs) {
    const role = input.role ?? input.key;
    const asset = draft.sources[role === "source_image" ? "front" : role];
    if (asset) sources[role] = asset;
  }
  return { prompt: draft.prompt, settings, explicit, sources };
}

export function textureQuality(variant: MediaToolVariant, draft: ThreeDDraft) {
  const field = variant.settings.find(item => item.key === "texture_quality");
  if (!field) return undefined;
  const canDisable = variant.settings.some(item => item.key === "texture" && item.type === "boolean");
  return {
    value: canDisable && draft.settings.texture === false ? "textureless" : String(draft.settings.texture_quality ?? field.default ?? "standard"),
    options: [
      ...(canDisable ? [{ value: "textureless", get label() { return localeText("interface.textureless_90d422f1", { defaultValue: "Textureless" }); } }] : []),
      ...(field.enum ?? []).filter(value => ["standard", "detailed"].includes(value)).map(value => ({ value, label: value === "detailed" ? "Detailed" : "Standard" })),
    ],
  };
}

/** Textureless is a coherent setting combination, not an extra provider enum. */
export function textureQualitySettings(value: string): Record<string, SettingValue> {
  return { texture: value !== "textureless", pbr: value !== "textureless", texture_quality: value === "textureless" ? "standard" : value };
}

export function faceLimitBounds(variant: MediaToolVariant, draft: ThreeDDraft) {
  const field = variant.settings.find(item => item.key === "face_limit");
  if (!field) return undefined;
  const s = draft.settings;
  const maximum = s.smart_low_poly === true ? (s.quad === true ? 10_000 : 20_000)
    : s.quad === true ? 150_000 : s.geometry_quality === "detailed" ? 2_000_000 : 1_500_000;
  return { min: Math.max(field.minimum ?? 1, s.smart_low_poly === true ? 500 : 1), max: Math.min(field.maximum ?? maximum, maximum) };
}
